import throttle from 'lodash/throttle';

import { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, EditorView, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { updateInlineSuggestion } from '../commands';
import { getPluginState as getInlineSuggestionsPluginState } from '../plugin-factory';
import { type InlineSuggestion } from '../states';

import { dateSuggestionHandler } from './date/date';
import { SUGGESTION_TOOLBAR_ANCHOR_CLASS } from './date/utils';
import type { SuggestionHandler, SuggestionMarker } from './types';

export class SuggestionManager {
	private THROTTLE_MS: number = 100;

	private readonly handlers: SuggestionHandler[] = [dateSuggestionHandler];

	private detectFirstSuggestion(state: EditorState): InlineSuggestion | null {
		for (const handler of this.handlers) {
			const match = handler.detect(state);
			if (match) {
				const suggestedNode = handler.createReplacement(match, state.schema);
				return { ...match, suggestedNode };
			}
		}
		return null;
	}

	private throttledDetection = throttle(
		(view: EditorView) => {
			const { state, dispatch } = view;
			const { inlineSuggestion: prevSuggestion } = getInlineSuggestionsPluginState(state);
			const suggestion = this.detectFirstSuggestion(state);

			if (prevSuggestion === null && suggestion === null) {
				return;
			}

			let decorationSet = DecorationSet.empty;

			if (suggestion !== null) {
				decorationSet = DecorationSet.create(
					state.doc,
					this.buildDecorationsForSuggestions(suggestion, state),
				);
			}
			updateInlineSuggestion(suggestion, decorationSet)(state, dispatch);
		},
		this.THROTTLE_MS,
		{ leading: false, trailing: true },
	);

	public scheduleScanForSuggestions(view: EditorView) {
		this.throttledDetection(view);
	}

	public cancelSuggestionScans() {
		this.throttledDetection.cancel();
	}

	private createMarkerDecoration(marker: SuggestionMarker): Decoration {
		let decorationClass = marker.cssClass || '';
		if (marker.style === 'underline') {
			decorationClass += ' inline-suggestion-underline';
		} else if (marker.style === 'highlight') {
			decorationClass += ' inline-suggestion-highlight';
		}

		return Decoration.inline(marker.from, marker.to, {
			class: decorationClass.trim(),
		});
	}

	private buildDecorationsForSuggestions(
		suggestion: InlineSuggestion,
		state: EditorState,
	): Decoration[] {
		const handler = this.handlers.find((h) => h.type === suggestion.type);
		if (!handler) {
			return [];
		}

		const markers = handler.createMarkers(suggestion, state);
		const markerDecorations = markers.map((marker) => this.createMarkerDecoration(marker));

		// Anchor decoration for the floating toolbar
		const anchorDecoration = Decoration.inline(suggestion.from, suggestion.to, {
			class: SUGGESTION_TOOLBAR_ANCHOR_CLASS,
		});

		return [...markerDecorations, anchorDecoration];
	}
}

export const suggestionManager = new SuggestionManager();
